import { useEffect, useState } from "react";
import { apiInstance } from "../../util/api-new";
import Button from "../../Component/Common/Button";
import { SubscriptionCard } from "../../Component/Common/SubscriptionCard";
import {
  completeSubscription,
  getSubscriptionPlans,
} from "../../store/organization/organization.action";
import { razorpay_api_key } from "../../util/config";
import { getAmountInSubunits } from "../../util/RazorpayCurrency";
import { useSelector } from "react-redux";
import PageContainer from "../../Component/Common/Page";
import Input from "../../Component/Common/Input";
import { applyCouponCode } from "../../store/coupon/coupon.action";
import dayjs from "dayjs";
import MultiButton from "../../Component/extra/MultiButton";
import {
  calculatePriceDifference,
  determineCycleChange,
} from "../../util/SubscriptionPlanChange";
import { useNavigate } from "react-router-dom";

const OrgSubscription = () => {
  const navigate = useNavigate();

  const { profile } = useSelector((state) => state.loggedUser);

  profile &&
    profile.role !== "org_owner" &&
    navigate("/org/dashboard", { replace: true });

  const [isFetching, setIsFetching] = useState(true);

  const [subscription, setSubscription] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  useEffect(() => {
    setIsFetching(true);
    apiInstance
      .get("organization/subscription/current")
      .then((res) => {
        setSubscription(res.data);
        setIsFetching(false);
      })
      .catch((err) => {
        setIsFetching(false);
      });

    apiInstance.get("organization/subscription/usage").then((res) => {
      setCurrentUsage(res.data);
    });
  }, []);

  return (
    <PageContainer>
      {isUpgrading ? (
        <div>
          <div className="d-flex justify-content-end">
            <Button onClick={() => setIsUpgrading(false)}>Back</Button>
          </div>
          <SubscriptionUpgrade
            currentPlan={subscription?.planId}
            currentSubscription={subscription}
          />
        </div>
      ) : (
        <div className="card rounded-4">
          {/* Header */}
          <div
            className="text-white px-4 py-5 rounded-top-4"
            style={{ backgroundColor: "#ff4d67" }}
          >
            <h1 className="display-6 fw-bold mb-2">Subscription Details</h1>
            <p className="text-primary-light">
              Manage your organization's subscription plan
            </p>
          </div>

          {subscription ? (
            <div className="card-body p-4">
              {/* Current Plan Overview */}
              <div className="row g-4 mb-5">
                <div className="col-lg-4">
                  <div className="border rounded-2 border-primary bg-primary-subtle">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title fw-semibold">Current Plan</h5>
                        <div className="d-flex gap-3">
                          {!subscription.amount ? (
                            <span className="badge fw-normal rounded-pill border border-success bg-success-subtle text-black">
                              Free Trial
                            </span>
                          ) : null}
                          <span
                            className={`badge rounded-pill ${
                              subscription.status === "active"
                                ? "bg-success text-white"
                                : subscription.status === "expired"
                                  ? "bg-danger text-white"
                                  : subscription.status === "trial"
                                    ? "bg-warning text-dark"
                                    : "bg-secondary text-white"
                            }`}
                          >
                            {subscription.status?.toUpperCase() || "ACTIVE"}
                          </span>
                        </div>
                      </div>
                      <div className="display-6 text-primary fw-bold mb-2 text-capitalize">
                        {subscription.planId.name || "No Plan"}
                      </div>
                      <div className="text-muted">
                        {subscription.planId.pricing.symbol || ""}{" "}
                        {subscription.planId.pricing.amount || 0}/
                        {subscription.planId.billingCycle || "month"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="border rounded-2 border-success bg-success-subtle">
                    <div className="card-body">
                      <h5 className="card-title fw-semibold mb-3">
                        Usage Statistics
                      </h5>
                      <div className="space-y-3">
                        {currentUsage?.limits.maxMembers ? (
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Members</small>
                              <span className="fw-medium">
                                {currentUsage?.usage?.membersAdded}/
                                {currentUsage.limits.maxMembers}
                              </span>
                            </div>
                            <div className="progress" style={{ height: "8px" }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${currentUsage?.percentages?.membersAdded}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : null}

                        {currentUsage?.limits.maxCourses ? (
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Courses</small>
                              <span className="fw-medium">
                                {currentUsage?.usage?.coursesCreated}/
                                {currentUsage.limits.maxCourses}
                              </span>
                            </div>
                            <div className="progress" style={{ height: "8px" }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${currentUsage?.percentages?.coursesCreated}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : null}

                        {currentUsage?.limits.maxVideos ? (
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Videos</small>
                              <span className="fw-medium">
                                {currentUsage?.usage?.videosAdded}/
                                {currentUsage.limits.maxVideos}
                              </span>
                            </div>
                            <div className="progress" style={{ height: "8px" }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${currentUsage?.percentages?.videosAdded}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="border rounded-2 border-info bg-info-subtle">
                    <div className="card-body">
                      <h5 className="card-title fw-semibold mb-3">
                        Plan Information
                      </h5>
                      <div className="space-y-2">
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">Started On</small>
                          <span className="fw-medium">
                            {dayjs(subscription.startDate).format(
                              "DD MMM YYYY",
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">Ends On</small>
                          <span className="fw-medium">
                            {dayjs(subscription.endDate).format("DD MMM YYYY")}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">Days Left</small>
                          <span className="fw-medium fs-5">
                            {dayjs(subscription?.endDate)
                              .startOf("day")
                              .diff(dayjs().startOf("day"), "day") >= 0 ? (
                              dayjs(subscription.endDate).diff(
                                dayjs(),
                                "days",
                              ) + 1
                            ) : (
                              <span className="text-danger">Expired</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div className="mb-5">
                <h4 className="fw-semibold mb-4">Plan Features</h4>
                <div className="row g-3">
                  {subscription?.planId?.features?.map((feature, index) => (
                    <div key={index} className="col-md-6">
                      <div className="d-flex align-items-center p-3 bg-light rounded">
                        <div className="flex-shrink-0 me-3">
                          <div
                            className="rounded-circle bg-success d-flex align-items-center justify-content-center"
                            style={{ width: "24px", height: "24px" }}
                          >
                            <i
                              className="bi bi-check text-white"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </div>
                        </div>
                        <span className="text-body">{feature.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex flex-column flex-sm-row gap-3">
                <Button onClick={() => setIsUpgrading(true)}>
                  {subscription.status === "active"
                    ? "Upgrade Plan"
                    : "Renew Plan"}
                </Button>
              </div>
            </div>
          ) : isFetching ? null : (
            <div className="card-body p-5 text-center">
              <div className="mx-auto" style={{ maxWidth: "400px" }}>
                <h4 className="fw-semibold mb-3">No Active Subscription</h4>
                <p className="text-muted mb-4">
                  You don't have any active subscription. Choose a plan that
                  fits your organization's needs to unlock all features.
                </p>
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-primary fw-medium"
                    onClick={() => setIsUpgrading(true)}
                  >
                    View Available Plans
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

const SubscriptionUpgrade = ({ currentPlan, currentSubscription }) => {
  const { orgProfile } = useSelector((state) => state.organization);

  const [allPlans, setAllPlans] = useState([]);
  const [updatedPlans, setUpdatedPlans] = useState([]);
  const [selectedPlan, setselectedPlan] = useState();
  const [currentTab, setCurrentTab] = useState("Monthly");

  const [couponCode, setCouponCode] = useState();
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const fetchSubscriptionPlans = async () => {
    const plans = await getSubscriptionPlans();
    setAllPlans(plans.map((plan) => ({ ...plan, hasTrial: false })) || []);
  };

  const handleApplyCouponCode = async () => {
    if (couponCode) {
      const res = await applyCouponCode(
        couponCode,
        selectedPlan._id,
        orgProfile._id,
        selectedPlan?.newPrice || selectedPlan.pricing.amount,
      );

      setAppliedCoupon(res.data || null);
      setCouponCode("");
    }
  };

  const reApplyCouponCodeOnPlanChange = async () => {
    if (appliedCoupon) {
      const res = await applyCouponCode(
        appliedCoupon.coupon.code,
        selectedPlan._id,
        orgProfile._id,
        selectedPlan?.newPrice || selectedPlan.pricing.amount,
      );

      setAppliedCoupon(res.data || null);
      setCouponCode("");
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  const handlePayment = async () => {
    if (selectedPlan) {
      const options = {
        key: razorpay_api_key,
        amount: getAmountInSubunits(
          appliedCoupon
            ? appliedCoupon.finalAmount
            : selectedPlan.newPrice || selectedPlan.pricing.amount,
          selectedPlan.pricing.currency,
        ),
        currency: selectedPlan.pricing.currency,
        name: "Care Tube",
        description: "Care Tube",
        image: "../../assets/images/MetubeLogo.png",
        handler: async function (response) {
          const payload = {
            planId: selectedPlan._id,
            razorpayPaymentId: response.razorpay_payment_id,
            currentPlanId: currentPlan?._id,
            couponData: appliedCoupon
              ? {
                  couponId: appliedCoupon.coupon._id,
                  originalAmount: appliedCoupon.originalAmount,
                  discountAmount: appliedCoupon.discountAmount,
                  finalAmount: appliedCoupon.finalAmount,
                }
              : null,
            paymentDetails: {
              amount: selectedPlan.pricing.amount,
              upgradeDiscount:
                selectedPlan.pricing.amount -
                (selectedPlan.newPrice || selectedPlan.pricing.amount),
              currency: selectedPlan.pricing.currency,
              currencySymbol: selectedPlan.pricing.currencySymbol,
              paymentGateway: "razorpay",
              transactionId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              billingAddress: {
                name: orgProfile.name,
                email: orgProfile.email,
                phone: orgProfile.phone,
                address: orgProfile.address,
              },
            },
          };

          let startDate, endDate;

          if (
            currentSubscription.status === "active" &&
            currentSubscription.planId.billingCycle ===
              selectedPlan.billingCycle
          ) {
            startDate = dayjs(currentSubscription.startDate).toDate();
            endDate = dayjs(currentSubscription.startDate)
              .add(selectedPlan.billingCycle == "yearly" ? 365 : 30, "days")
              .subtract(1, "day")
              .endOf("day")
              .toDate();
          } else {
            startDate = dayjs().toDate();
            endDate = dayjs()
              .add(selectedPlan.billingCycle == "yearly" ? 365 : 30, "days")
              .subtract(1, "day")
              .endOf("day")
              .toDate();
          }

          payload.startDate = startDate;
          payload.endDate = endDate;

          const res = await completeSubscription(payload);

          if (res) {
            window.location.reload();
          }
        },
        prefill: {
          name: orgProfile.name,
          email: orgProfile.email,
        },
        notes: {
          ...(appliedCoupon && {
            couponId: appliedCoupon.coupon._id,
            originalAmount: appliedCoupon.originalAmount,
            discountAmount: appliedCoupon.discountAmount,
            finalAmount: appliedCoupon.finalAmount,
          }),
        },
        theme: {
          color: "#ff4d67",
        },
        modal: {
          ondismiss: function () {},
        },
      };

      const rzp = new Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.error("Payment Error:", response.error);
      });

      rzp.open();
    }
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    let newPlans = [];

    allPlans.forEach((plan) => {
      // calculate number of days left in subscription
      if (currentSubscription.status === "active") {
        const oldPlanPrice = currentSubscription.planId.pricing.amount;
        const newPlanPrice = plan.pricing.amount;
        const daysLeft =
          dayjs(currentSubscription?.endDate).diff(dayjs(), "day") + 1;
        const currentSubscriptionDays =
          currentSubscription?.planId.billingCycle === "monthly" ? 30 : 365;
        const numberOfDaysLeftInNewPlan =
          (plan.billingCycle === "monthly" ? 30 : 365) -
          (currentSubscriptionDays - daysLeft);

        const cycleChange = determineCycleChange(
          currentSubscription.planId.billingCycle,
          plan.billingCycle,
        );
        const newPrice = calculatePriceDifference(
          cycleChange,
          oldPlanPrice,
          newPlanPrice,
          currentSubscriptionDays,
          daysLeft,
          numberOfDaysLeftInNewPlan,
        );

        newPlans.push({
          ...plan,
          newPrice: newPrice,
          hasTrial: false,
        });
      } else {
        newPlans.push({
          ...plan,
          hasTrial: false,
        });
      }
    });

    setUpdatedPlans(newPlans.filter((plan) => plan.newPrice > 0));
    setselectedPlan(
      newPlans.filter((plan) => plan.newPrice > 0)[0] ||
        currentSubscription.planId,
    );
    setCurrentTab(
      currentSubscription?.planId?.billingCycle[0].toUpperCase() +
        currentSubscription?.planId?.billingCycle.slice(1),
    );
  }, [allPlans, currentSubscription]);

  useEffect(() => {
    reApplyCouponCodeOnPlanChange();
  }, [selectedPlan]);

  useEffect(() => {
    // Reset selected plan when tab changes
    setselectedPlan(
      displayPlans.find((plan) => plan?.name === selectedPlan?.name),
    );
  }, [currentTab]);

  const displayPlans = (
    currentSubscription?.status === "active" ? updatedPlans : allPlans
  ).filter((plan) => plan.billingCycle === currentTab.toLowerCase());

  return (
    <div>
      <h2>Subscription</h2>
      <h5>
        {currentSubscription?.status === "active"
          ? displayPlans.length > 0
            ? "Just pay price difference and upgrade your plan"
            : ""
          : "Choose a plan that fits your organization's needs to unlock all features."}
      </h5>
      <br />

      {currentSubscription?.planId?.billingCycle !== "yearly" && (
        <div className="d-flex flex-column align-items-center justify-content-center mb-4">
          <MultiButton
            label={["Monthly", "Yearly"]}
            multiButtonSelect={currentTab}
            setMultiButtonSelect={setCurrentTab}
          />
          {currentSubscription?.status === "active" && (
            <p className="text-secondary">
              Upgrading from Monthly to Yearly will start from today
            </p>
          )}
        </div>
      )}

      <div className="row g-4">
        {currentSubscription?.status === "active" ? (
          displayPlans.length > 0 ? (
            displayPlans.map((plan) => (
              <div key={plan._id} className="col-12 col-md-6 col-xl-4">
                <SubscriptionCard
                  isSelected={selectedPlan?._id === plan?._id}
                  onSelect={setselectedPlan}
                  planData={plan}
                />
              </div>
            ))
          ) : (
            <p className="p-5 text-center">No plans for you </p>
          )
        ) : (
          displayPlans.map((plan) => (
            <div key={plan._id} className="col-12 col-md-6 col-xl-4">
              <SubscriptionCard
                isSelected={selectedPlan?._id === plan?._id}
                onSelect={setselectedPlan}
                planData={plan}
              />
            </div>
          ))
        )}
      </div>

      {currentSubscription?.status === "active" &&
      displayPlans.length <= 0 ? null : (
        <div className="d-flex flex-column justify-content-center align-items-end gap-2 mt-5">
          {appliedCoupon ? (
            <div className="mt-2 text-end">
              <button
                className="btn btn-outline-danger mb-3"
                type="button"
                onClick={() => handleRemoveCoupon()}
              >
                Remove
              </button>
              <div className="d-flex align-items-center justify-content-end gap-2">
                <p className="fw-semibold">Original Amount:</p>
                <p className="text-end" style={{ width: "60px" }}>
                  {appliedCoupon.originalAmount.toFixed(2)}
                </p>
              </div>
              <div className="d-flex align-items-center justify-content-end gap-2">
                <p className="fw-semibold">Discount Amount:</p>
                <p className="text-end" style={{ width: "60px" }}>
                  {appliedCoupon.discountAmount.toFixed(2)}
                </p>
              </div>
              <div className="d-flex align-items-center justify-content-end gap-2">
                <p className="fw-semibold">Total :</p>
                <p className="text-end" style={{ width: "60px" }}>
                  {appliedCoupon.finalAmount.toFixed(2)}
                </p>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <Input
                placeholder="Coupon Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />

              <Button
                className="rounded"
                type="button"
                disabled={!couponCode}
                onClick={handleApplyCouponCode}
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      )}

      {currentSubscription?.status === "active" &&
      displayPlans.length <= 0 ? null : (
        <div className="mt-5 d-flex justify-content-end">
          <Button onClick={handlePayment} disabled={!selectedPlan}>
            Pay
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrgSubscription;
